import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { MenuItem, MessageService } from 'primeng/api';
import {
  MediaItem,
  TravelItem,
  TravelItemMinimal,
  UserItemEssentials,
} from '../../../api/models';
import { BookingService, TravelService } from '../../../api/services';
import {
  ActivatedRoute,
  NavigationStart,
  Params,
  Router,
} from '@angular/router';
import {
  addDays,
  endOfYesterday,
  isAfter,
  isBefore,
  isWithinInterval,
  max,
  startOfDay,
  startOfToday,
  subDays,
} from 'date-fns';
import { AuthService } from '../../../guards/services/auth.service';
import { environment } from '../../../../environments/environment';
import { SlugifyPipe } from '../../../shared/pipes/slugify.pipe';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { ModalShareOnSocialsComponent } from '../../../shared/modals/modal-share-on-socials/modal-share-on-socials.component';
import { enumAssignmentStatus } from '../../../shared/utils/enum-assignment-request';
import { Location } from '@angular/common';

@Component({
  selector: 'app-travel-page',
  templateUrl: './travel-page.component.html',
  styleUrl: './travel-page.component.scss',
})
export class TravelPageComponent {
  items: MenuItem[] | undefined;
  showTravelProgram = false;
  responsiveOptions: any[] | undefined;
  suggested_travels: TravelItemMinimal[] = [];
  travelMenuIndicator?: any;
  travel?: TravelItem;
  dates: string[] = [];
  displayBasic = false;
  insuranceTextDialog = false;
  environmentMediaUrl = environment.mediaUrl;
  isInFavs = false;
  travel_id?: number;
  today = new Date().toISOString();
  travelers: UserItemEssentials[] = [];
  limit = 5;
  skip = 0;
  totalRecords = 0;
  shareOnSocialRef: DynamicDialogRef | undefined;
  loading = false;
  luggage_attachment?: MediaItem;
  cancellation_attachment?: MediaItem;
  enumAssignmentStatus = enumAssignmentStatus;
  cheapestPackagePrice?: number;
  showButtons: boolean = false;
  visible: boolean = false; // per modale
  termsAndConditions: boolean = false; // per modale

  constructor(
    private title: Title,
    private travelService: TravelService,
    private ar: ActivatedRoute,
    private router: Router,
    public auth: AuthService,
    private messageService: MessageService,
    private slugifyPipe: SlugifyPipe,
    private bookingService: BookingService,
    public dialogService: DialogService,
    private location: Location,
    private meta: Meta
  ) {}

  ngOnInit() {
    this.ar.params.subscribe((p: Params) => {
      if (p) {
        if (p['iso']) {
          this.travel_id = p['id'];
        } else if (p['my_travel_id']) {
          this.travel_id = p['my_travel_id'];
        } else if (p['my_fav_id']) {
          this.travel_id = p['my_fav_id'];
        }
        this.getOne();
      }
      if (this.auth.isLogged()) {
        this.auth.getMyself();
        if (this.auth.user.value!.favs!.includes(Number(this.travel_id))) {
          this.isInFavs = true;
        } else {
          this.isInFavs = false;
        }
      }
    });

    this.responsiveOptions = [
      {
        breakpoint: '1399px',
        numVisible: 3,
        numScroll: 1,
      },
      {
        breakpoint: '1199px',
        numVisible: 2,
        numScroll: 1,
      },
      {
        breakpoint: '991px',
        numVisible: 2,
        numScroll: 1,
      },
      {
        breakpoint: '767px',
        numVisible: 1,
        numScroll: 1,
      },
    ];
  }

  getOne() {
    this.loading = true;
    this.travelService
      .travelControllerGetTravel({ id: this.travel_id! })
      .subscribe({
        next: (res) => {
          this.travel = res.data; //inverto le img caricate dal BE cosi da mostrare sempre la prima selezionata
          const slugifyNameTravel = this.slugifyPipe.transform(
            this.travel!.name
          );
          this.meta.addTag({
            property: 'og:title',
            content: `${this.travel.name}`,
          });
          this.meta.addTag({
            property: 'og:description',
            content: `${this.travel.payoff}`,
          });
          if (this.travel.medias.length !== 0) {
            // console.log(this.travel.medias[0].media.path)
            if (this.travel.medias[0].media.path.includes('https://')) {
              this.meta.addTag({
                property: 'og:image',
                content: `${this.travel.medias[0].media.path}`,
              });
            } else {
              this.meta.addTag({
                property: 'og:image',
                content: `${environment.mediaUrl}/${this.travel.medias[0].media.path}`,
              });
            }
          }
          this.meta.addTag({
            property: 'og:url',
            content: `${environment.shareBaseUrl}/viaggi-di-gruppo/${
              this.travel!.countries[0].country.ISO
            }/${this.travel!.countries[0].country.name}/${
              this.travel!.id
            }/${slugifyNameTravel}`,
          });

          this.dates.push(res.data.from_date);
          this.dates.push(res.data.to_date);
          this.title.setTitle(
            'SocialTravels - Viaggio' + ' - ' + this.travel?.name
          );
          this.setBreadcrumb();
          if (res.data.undisclosed_members_list === false) {
            this.getTravelers();
          }
          //ALLEGATI
          if (this.travel?.attachments) {
            this.luggage_attachment = this.travel.attachments.find((x) =>
              x.attachment.path.includes('luggage-insurance')
            )?.attachment;
            this.cancellation_attachment = this.travel.attachments.find((x) =>
              x.attachment.path.includes('cancellation-insurance')
            )?.attachment;
          }
          //PRICE
          if (
            this.travel?.price_packages &&
            this.travel?.price_packages.length > 0
          ) {
            const prices = this.travel.price_packages.map(
              (p) => p.price_per_person
            );
            this.cheapestPackagePrice = Math.min(...prices);
          }

          this.ar.queryParams.subscribe((p: Params) => {
            if (p['share'] === 'on') {
              this.openShareOnSocialsModal();
            }
          });

          this.loading = false;
        },
      });
  }

  openShareOnSocialsModal() {
    const slugifyCountryTravel = this.slugifyPipe.transform(
      this.travel!.countries[0].country.name
    );
    const slugifyNameTravel = this.slugifyPipe.transform(this.travel!.name);
    this.shareOnSocialRef = this.dialogService.open(
      ModalShareOnSocialsComponent,
      {
        header: 'Condividi questo viaggio con chi vuoi!',
        width: '50%',
        styleClass: 'modal-res',
        data: {
          travel: this.travel,
          url: `${environment.shareBaseUrl}/viaggi-di-gruppo/${
            this.travel!.countries[0].country.ISO
          }/${slugifyCountryTravel}/${this.travel!.id}/${slugifyNameTravel}`,
        },
      }
    );
    this.shareOnSocialRef.onClose.subscribe((v) => {
      this.router.navigate([
        `/viaggi-di-gruppo/${this.travel!.countries[0].country.ISO}/${
          this.travel!.countries[0].country.name
        }/${this.travel!.id}/${slugifyNameTravel}`,
      ]);
    });
    // this.shareOnSocialRef.onClose.subscribe((v: {readyForPrinting: boolean}) => {
    //   if (v?.readyForPrinting) {
    //     setTimeout(() => {
    //       window.print();
    //     }, 100);
    //   }
    // })
  }

  setBreadcrumb() {
    this.ar.params.subscribe((p: Params) => {
      if (p) {
        if (p['iso']) {
          const iso = p['iso'];
          const name = p['name'];
          this.items = [
            { label: 'Homepage', routerLink: '/' },
            { label: name, routerLink: ['/viaggi-di-gruppo', iso, name] },
            { label: this.travel?.name },
          ];
        } else {
          this.items = [
            { label: 'Homepage', routerLink: '/' },
            {
              label: this.travel!.countries[0].country.name,
              routerLink: [
                '/viaggi-di-gruppo',
                this.travel!.countries[0].country.ISO,
              ],
            },
            { label: this.travel?.name },
          ];
        }
      }
    });

    this.getSuggestedTravels();
  }

  scrollTo(to: HTMLElement) {
    to.scrollIntoView({
      behavior: 'smooth',
    });
  }

  checkDatesForAlert(): boolean {
    const d = new Date(this.travel?.from_date!);
    return this.isTodayWithinRange(d);
  }

  isTodayWithinRange(depDate: Date): boolean {
    const today = startOfToday();
    const endDate = subDays(depDate, 14); // Sottrae 14 giorni da depDate
    const interval = { start: endDate, end: depDate };

    return isWithinInterval(today, interval);
  }

  checkForAvailability() {
    const maxMembers = this.travel?.max_members!;
    const currentMembers = this.travel?.availability?.booked!;
    return this.isGreaterThanPercentage(maxMembers, currentMembers, 60);
  }

  isGreaterThanPercentage(X: number, Y: number, percentage: number): boolean {
    if (X > Y) {
      const threshold = X * (percentage / 100);
      return Y > threshold;
    } else {
      return false;
    }
  }

  addToFavs(id: number) {
    this.travelService.travelControllerAddFavourite({ id: id }).subscribe({
      next: (res) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Preferiti',
          detail: 'Il viaggio è stato aggiunto ai preferiti.',
        });
        this.isInFavs = true;
      },
      error: (err: Error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Preferiti',
          detail: 'Impossibile aggiungere il viaggio ai preferiti.',
        });
      },
    });
  }

  removeFromFavs(id: number) {
    this.travelService.travelControllerRemoveFavourite({ id: id }).subscribe({
      next: (res) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Preferiti',
          detail: 'Il viaggio è stato rimosso dai preferiti.',
        });
        this.isInFavs = false;
      },
      error: (err: Error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Preferiti',
          detail: 'Impossibile rimuovere il viaggio dai preferiti',
        });
      },
    });
  }

  getSuggestedTravels() {
    this.travelService
      .travelControllerGetAll({
        limit: 10,
        skip: 0,
        countries: this.travel?.countries.map((x) => x.country.ISO),
      })
      .subscribe({
        next: (res) => {
          this.suggested_travels = res.data.list.filter(
            (x) => x.id != this.travel_id
          );
        },
      });
  }

  goToCheckout() {
    const slugifyName = this.slugifyPipe.transform(this.travel!.name);
    // console.log(slugifyName)
    if (!this.travel!.funding) {
      this.router.navigate([
        '/viaggi-di-gruppo/join',
        slugifyName,
        this.travel!.id,
      ]);
    } else {
      this.router.navigate([
        '/viaggi-di-gruppo/join-gmg',
        slugifyName,
        this.travel!.id,
      ]);
    }
    // TODO INSERIRE ROTTA NUOVO COMPONENTE CHECKOUT PER FUNDING TRUE
  }

  goToRegister() {
    this.router.navigate(['/login'], {
      queryParams: { back_to_url: this.router.url },
    });
  }

  goToDonate() {
    const slugifyName = this.slugifyPipe.transform(this.travel!.name);
    this.router.navigate([
      '/viaggi-di-gruppo/join-donate',
      slugifyName,
      this.travel!.id,
    ]);
  }

  isAvailable() {
    const addedDays = addDays(this.today, 10);
    //Se le date del viaggio sono nel passato
    if (this.travel?.from_date && this.travel?.to_date < this.today) {
      return true;
    }
    //Se la partenza del viaggio è nei prossimi 7 giorni
    else if (this.travel!.from_date < addedDays.toISOString()) {
      return true;
    }

    return false;
  }

  isPaymentAvailable() {
    const today = new Date();
    const nextMonth = addDays(today, 31);
    if (this.travel!.from_date < nextMonth.toISOString()) {
      return false;
    } else {
      return true;
    }
  }

  getTravelers() {
    this.bookingService
      .bookingControllerTravelTravelers({
        limit: this.limit,
        skip: this.skip,
        travel_id: this.travel?.id!,
      })
      .subscribe({
        next: (res) => {
          this.travelers = res.data.list;
          this.totalRecords = res.data.count;
        },
      });
  }

  alreadyJoined() {
    if (this.travelers.find((x) => x.id === this.auth.getUserId())) {
      return true;
    } else {
      return false;
    }
  }

  loadMoreTravelers() {
    this.limit += this.limit;
    this.getTravelers();
  }

  goTo(where?: string) {
    const slugifyNameTravel = this.slugifyPipe.transform(this.travel!.name);
    if (where) {
      if (where === '/guest') {
        this.router.navigate([
          `/viaggi-di-gruppo/join-guest/${slugifyNameTravel}/${
            this.travel!.id
          }`,
        ]);
      } else {
        if (this.travel && this.travel.countries.length > 0) {
          sessionStorage.setItem(
            'book_item_redirect',
            `/viaggi-di-gruppo/${this.travel.countries[0].country.ISO}/${this.travel.countries[0].country.name}/${this.travel.id}/${slugifyNameTravel}`
          );
          this.router.navigate(['/login']);
        }
      }
    } else {
      const slugifyNamePlayer = this.slugifyPipe.transform(
        this.travel!.player.name
      );
      this.router.navigate(['/business', slugifyNamePlayer]);
    }
  }

  getTravel_metadata(i: number) {
    let travel_metadata;
    if (this.travel?.medias && this.travel.medias.length > 0) {
      if (this.travel.medias[i].media.metadata) {
        travel_metadata = this.travel.medias[i].media.metadata;
      }
    }
    if (travel_metadata) {
      return travel_metadata as any;
    } else {
      return;
    }
  }
}
